<script setup lang="ts">
import { computed } from "vue";
import { useTeamStore } from "@/stores/teams.ts";
import { type GameTeam, useGameStore } from "@/stores/games.ts";
import { useGroupStore } from "@/stores/groups.ts";

const props = defineProps<{
  team: {
    name: string;
    logo_url: string;
  } | GameTeam | string;
  hide_name?: boolean;
  class?: string;
}>();

const teamStore = useTeamStore();
const gameStore = useGameStore();
const groupStore = useGroupStore();

const team = computed<{
  name: string;
  logo_url: string;
  hint?: string;
}>(() => {
  if (typeof props.team === "string") {
    return teamStore.getTeamById(props.team);
  }
  if (typeof props.team === "object") {
    if ('name' in props.team && 'logo_url' in props.team) {
      return props.team;
    } else if ('id' in props.team && 'game' in props.team && 'placement' in props.team) {
      let hint = undefined;
      if (props.team.game) {
        const gameId = props.team.game.id;
        const game = gameStore.games.find(g => g.id === gameId);
        if (game) {
          const gameIndex = gameStore.games.indexOf(game) + 1;
          hint = `${props.team.game.win ? "Gewinner" : "Verlierer"} von Spiel ${gameIndex}`;
        }
      } else if (props.team.placement) {
        const groupId = props.team.placement.id;
        const group = groupStore.groups.find(g => g.id === groupId);
        if (group) {
          hint = `Platz ${props.team.placement.place} in Gruppe ${group.name}`;
        }
      }
      if (props.team.id) {
        const teamData = teamStore.getTeamById(props.team.id);
        return {
          name: teamData.name,
          logo_url: teamData.logo_url,
          hint: hint
        };
      }
      const unknownTeam = teamStore.getTeamById("");
      return {
        name: !hint ? unknownTeam.name : hint,
        logo_url: unknownTeam.logo_url,
      };
    }
  }
  console.error("Invalid team prop provided to TeamDisplay");
  return teamStore.getTeamById("");
});

</script>

<template>
  <div class="team-display" :class="props.class">
    <img class="team-logo" :src="team.logo_url" :alt="`Logo: ${team.name}`" />
    <div v-if="!props.hide_name" class="team-name">
      <p>{{ team.name }}</p>
      <p v-if="team.hint" class="team-hint">{{ team.hint }}</p>
    </div>
  </div>
</template>

<style scoped>
.team-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.team-logo {
  display: block;
  height: 2rem;
  width: 2rem;
  object-fit: contain;
  border-radius: 50%;
  background-color: white;
  overflow: hidden;
  flex-shrink: 0;
}

.team-hint {
  font-size: 0.8rem;
  opacity: 0.6;
  margin-top: -0.375rem;
}
</style>
